import React, { Fragment } from 'react';
import Services from '../../api/service'
import { useParams, Navigate } from 'react-router-dom'
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle'
import Scrollbar from '../../components/scrollbar/scrollbar'
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import ProcessTechnology from '../../components/software-company-components/ProcessTechnology/ProcessTechnology';
import WebDevPromo from '../../components/WebDevPromo/WebDevPromo';
import SurveillanceBrands from '../../components/SurveillanceBrands/SurveillanceBrands';
import srImg from '../../images/services/service_details_image_1.webp'
import srImg2 from '../../images/services/service_details_image_2.webp'
import srImg3 from '../../images/services/service_details_image_3.webp'
import srImg4 from '../../images/services/service_details_image_4.webp'
import aiImg1 from '../../images/services/buildlab_ai_hr.jpg'
import aiImg2 from '../../images/services/buildlab_ai_accounting.jpg'
import aiImg3 from '../../images/services/buildlab_ai_workflows.jpg'
import webDevHero from '../../images/services/buildlab_web_dev_hero.jpg'
import marketingHero from '../../images/services/buildlab_marketing_hero.jpg'
import webDev1 from '../../images/services/buildlab_webdev_1.jpg'
import webDev2 from '../../images/services/buildlab_webdev_2.jpg'
import webDev3 from '../../images/services/buildlab_webdev_3.jpg'
import mkt1 from '../../images/services/buildlab_marketing_1.jpg'
import mkt2 from '../../images/services/buildlab_marketing_2.jpg'
import mkt3 from '../../images/services/buildlab_marketing_3.jpg'
import surv1 from '../../images/services/buildlab_surveillance_1.jpg'
import surv2 from '../../images/services/buildlab_surveillance_2.jpg'
import surv3 from '../../images/services/buildlab_surveillance_3.jpg'
import survHero from '../../images/services/buildlab_surveillance_hero.jpg'
import cablingHero from '../../images/services/buildlab_structured_cabling.jpg'
import icon from '../../images/icons/icon_check_3.svg'

// Slugs folded into the consolidated IT Services page.
const IT_SERVICES_LEGACY_SLUGS = [
    'Cyber-Security',
    'Systems-and-Network-Administration',
    'IT-Infrastructure-and-Cloud',
];

// Per-service hero image override (falls back to the shared service_details_image_1).
const HERO_BY_SLUG = {
    'Web-Development': webDevHero,
    'Marketing-and-SEO': marketingHero,
    'Surveillance-Systems': survHero,
    'Low-Voltage-and-Structured-Cabling': cablingHero,
};

// Per-service trio of detail images (falls back to the shared service_details_image_2/3/4).
const DETAIL_TRIO_BY_SLUG = {
    'Surveillance-Systems': [
        { src: surv1, alt: 'Video surveillance cameras on a commercial glass building' },
        { src: surv2, alt: 'Security operator monitoring live CCTV camera feeds in a control room' },
        { src: surv3, alt: 'Dome surveillance camera installed on a ceiling' },
    ],
    'Web-Development': [
        { src: webDev1, alt: 'Web design in progress' },
        { src: webDev2, alt: 'Clean, maintainable code' },
        { src: webDev3, alt: 'Responsive across devices' },
    ],
    'Marketing-and-SEO': [
        { src: mkt1, alt: 'Search engine optimization and keyword research' },
        { src: mkt2, alt: 'Content and campaign strategy planning' },
        { src: mkt3, alt: 'Digital marketing and social campaigns' },
    ],
};

// Per-service intro paragraphs (rendered below the hero). Falls back to the
// description from service.js plus a generic category sentence.
const INTRO_BY_SLUG = {
    'Marketing-and-SEO': [
        "Marketing & SEO at BUILDLAB is about getting found by the right people and turning that visibility into real business — not chasing vanity metrics. We combine technical SEO, content strategy, and digital marketing into one program built around the keywords your customers actually search and the outcomes your business actually cares about: calls, leads, and revenue.",
        "Because we build the websites too, our SEO isn't bolted on after the fact — it's engineered into the site structure, page speed, and content from day one. We start with a full audit of where you rank today, map the gaps against your competitors, and wire in analytics so every change is measured. No long-term lock-in and no jargon — just a clear plan and monthly reporting you can actually read.",
    ],
};

// Per-service "What you can expect" content. Falls back to category-based defaults.
const EXPECT_BY_SLUG = {
    'Marketing-and-SEO': {
        intro: 'Every Marketing & SEO engagement is measured against real business outcomes — tracked in GA4 and Google Search Console, reported monthly, and refined as the numbers come in.',
        left: [
            'Technical SEO & site-speed audit',
            'Keyword research & content strategy',
            'On-page & local SEO optimization',
        ],
        right: [
            'Content & landing-page creation',
            'GA4 & Search Console reporting',
            'Monthly tracking & iteration',
        ],
    },
};

// Services whose page should NOT show the "We Use Technologies" software
// stack grid (TechnologySection inside ProcessTechnology) — a PHP/React/
// Laravel logo wall makes sense under a software service, not a physical
// low-voltage/security trade. Surveillance was excluded first (it shows its
// own SurveillanceBrands hardware wall instead), Web-Development next (its
// AI-mode variant renders elsewhere), and Low-Voltage-and-Structured-Cabling
// on 2026-07-30 per explicit staff direction ("remove the We Use
// Technologies section in the low-voltage and structured Cabling Design
// section of the website").
const HIDE_TECH_SLUGS = ['Web-Development', 'Surveillance-Systems', 'Low-Voltage-and-Structured-Cabling'];

// Per-service "Working Process / Our Approach" accordion content.
// Services not listed here fall back to the generic BUILDLAB process in ProcessTechnology.
const PROCESS_STEPS_BY_SLUG = {
    'Web-Development': [
        { title: 'Discovery & Planning', body: 'We start by understanding your goals, audience, and content — mapping the sitemap, brand, and what the site actually needs to do for your business before any design begins.' },
        { title: 'Design & UX', body: 'Wireframes and mockups for a clean, conversion-focused, mobile-first design that reflects your brand and guides visitors toward the action you care about.' },
        { title: 'Build & Development', body: 'We build a fast, responsive, SEO-structured site on a stack your team can actually maintain — no bloated page-builders, no templates that look like everyone else.' },
        { title: 'Content & SEO', body: 'Content integration, on-page SEO, image optimization, and Google Analytics wired in from launch — so the site is found and you can measure what it does.' },
        { title: 'Launch & Support', body: 'Testing across devices and browsers, secure deployment with SSL, a quick training walkthrough, and ongoing support so the site keeps performing after it goes live.' },
    ],
    'Marketing-and-SEO': [
        { title: 'Discovery & SEO Audit', body: 'We learn your business, audience, and competitors, then audit your current site, rankings, analytics, and search visibility — identifying exactly what is holding growth back before we touch anything.' },
        { title: 'Keyword & Strategy', body: 'We research the terms real customers actually search, map them to intent and to the right pages, and build a content and SEO roadmap tied directly to your business goals — not a generic checklist.' },
        { title: 'On-Page & Technical SEO', body: 'We fix the foundations: site structure, page speed, metadata, schema markup, internal linking, and Core Web Vitals — so search engines can find, understand, and rank your content.' },
        { title: 'Content & Campaigns', body: 'We produce the content, landing pages, and digital campaigns — search, social, and email — that attract the right audience and move them toward the action that matters to your business.' },
        { title: 'Measure & Optimize', body: 'We track rankings, traffic, leads, and conversions in GA4 and Search Console, report against real outcomes every month, and keep refining what works — and cutting what does not.' },
    ],
    'Computer-Networks-and-Common-Areas': [
        { title: 'Site Survey & Coverage Mapping', body: 'We walk the building, count users, devices, and AP locations, identify dead zones and high-density areas, and document how residents and operations teams will actually use the network.' },
        { title: 'Network Design', body: 'VLAN segmentation, WiFi 6/7 access-point placement and heat-mapping, switching and cabling backbone, and bandwidth/QoS planning so broadband, IoT, surveillance, and AV all share one network without fighting each other.' },
        { title: 'Procurement & Staging', body: 'We spec and source the right hardware — UniFi, Meraki, Cisco — then stage and pre-configure switches, access points, and firewalls before they ever reach the site.' },
        { title: 'Installation & Integration', body: 'We coordinate with the low-voltage and electrical trades, mount and terminate, and integrate the network with surveillance, door access, and common-area AV as one connected environment.' },
        { title: 'Testing & Handoff', body: 'We validate throughput and coverage in every space, test failover, document the as-built network, and train your operations team so it is fully supportable on day one.' },
    ],
};

// Per-service "Related free tools" links — contextual internal links from each
// service page to its matching high-intent calculators/checkers.
const RELATED_TOOLS_BY_SLUG = {
    'Low-Voltage-and-Structured-Cabling': [
        { label: 'Low-voltage budget calculator', to: '/low-voltage-budget-estimator' },
        { label: 'Request a low-voltage design & install proposal', to: '/low-voltage-project-request' },
    ],
    'Surveillance-Systems': [
        { label: 'Low-voltage budget calculator (includes surveillance)', to: '/low-voltage-budget-estimator' },
        { label: 'Request a surveillance design proposal', to: '/low-voltage-project-request' },
    ],
    'Door-Access-Systems': [
        { label: 'Low-voltage budget calculator (includes door access)', to: '/low-voltage-budget-estimator' },
        { label: 'Request a door access design proposal', to: '/low-voltage-project-request' },
    ],
    'Fire-and-Life-Safety': [
        { label: 'DAS / ERRCS requirement checker', to: '/das-erccs-checker' },
        { label: 'Area of refuge requirements checker', to: '/area-of-refuge-checker' },
    ],
    'AI-Business-Integration': [
        { label: 'AI readiness assessment', to: '/ai-readiness-assessment' },
    ],
    'Computer-Networks-and-Common-Areas': [
        { label: 'Low-voltage budget calculator', to: '/low-voltage-budget-estimator' },
    ],
};

const AI_PROCESS_STEPS = [
    {
        title: 'Discovery & Workflow Audit',
        body: 'We sit with your team and map the daily, weekly, and monthly tasks that eat the most time — the manual reports, repetitive emails, lookups, approvals, and handoffs. The output is a workflow map showing where the hours actually go.',
    },
    {
        title: 'AI Opportunity Mapping',
        body: 'For each workflow, we identify where AI assistants, copilots, or lightweight automation can absorb the work — and we flag the places where AI would over-engineer the problem and a simple fix is better.',
    },
    {
        title: 'Tool & Stack Recommendations',
        body: 'A short list of the AI tools that fit your business — Microsoft Copilot, Claude, ChatGPT Business, Notion AI, custom GPTs, or purpose-built automations — with realistic cost, rollout effort, and adoption risk for each.',
    },
    {
        title: 'Team Training & Rollout',
        body: 'Hands-on training sessions, prompt patterns your team can actually reuse, and the governance and guardrails (data privacy, output review, ownership) that turn AI from a demo into a daily habit.',
    },
    {
        title: 'Measure, Iterate, Expand',
        body: 'Quantify hours saved per role, refine prompts and workflows based on real usage, and expand into new corners of your business once the first wins are sticking.',
    },
];


const ServiceSinglePage = (props) => {

    const { slug } = useParams()

    const ServiceDetails = Services.find(item => item.slug === slug)

    usePageSeo({
        title: ServiceDetails?.seoTitle || ServiceDetails?.title || 'Service Details',
        description: ServiceDetails?.metaDescription
            || (ServiceDetails?.description
                ? (ServiceDetails.description.length > 158
                    // Truncate at a word boundary only when actually over the SERP limit.
                    ? ServiceDetails.description.slice(0, 158).replace(/\s+\S*$/, '')
                    : ServiceDetails.description)
                : `${ServiceDetails?.title || 'BUILDLAB service'} — technology consulting and design from BUILDLAB.`),
        keywords: `${ServiceDetails?.title || 'BUILDLAB'}, technology consulting, ${ServiceDetails?.category === 'construction' ? 'affordable housing, construction technology' : 'business IT, managed IT'}`,
    });

    // Old Cyber Security / Systems & Network / Infrastructure pages now live
    // under the consolidated IT Services page. (After hooks — never conditional.)
    if (IT_SERVICES_LEGACY_SLUGS.includes(slug) || !ServiceDetails) {
        return <Navigate to="/it-services" replace />;
    }

    // Three detail images: AI page uses AI infographics, Web Development uses its own
    // web-dev trio, everything else uses the shared service detail images.
    const detailTrio = slug === 'AI-Business-Integration'
        ? [
            { src: aiImg1, alt: 'AI-Driven HR Automation' },
            { src: aiImg2, alt: 'AI Automation in Accounting' },
            { src: aiImg3, alt: 'AI & Machine Learning: Automating Business Workflows' },
        ]
        : (DETAIL_TRIO_BY_SLUG[slug] || [
            { src: srImg2, alt: ServiceDetails.title },
            { src: srImg3, alt: ServiceDetails.title },
            { src: srImg4, alt: ServiceDetails.title },
        ]);

    // Intro paragraphs: per-slug override, else description + generic category sentence.
    const isConstruction = ServiceDetails.category === 'construction';
    const introParagraphs = INTRO_BY_SLUG[slug] || [
        ServiceDetails.description || `${ServiceDetails.title} is a core BUILDLAB service designed and delivered by our in-house team — scoped to the size and complexity of your project, with no upcharges or hidden fees.`,
        `BUILDLAB takes a hands-on approach: we sit with your stakeholders, scope to your real requirements, and coordinate every milestone through closeout. ${isConstruction ? 'For construction projects, that means designing to spec — eliminating over-ordering, redundant cabling, and the budget creep that comes from making technology decisions in the wrong phase.' : 'For businesses, that means understanding how your team actually works — so the technology supports day-to-day operations instead of fighting them.'}`,
    ];

    // "What you can expect" content: per-slug override, else category-based defaults.
    const expect = EXPECT_BY_SLUG[slug] || {
        intro: `Every ${ServiceDetails.title} engagement delivers measurable outcomes — scoped at the start, tracked through delivery, and documented for handoff.`,
        left: isConstruction
            ? ['Design to spec — no over-ordering', 'OAC coordination through closeout', 'Code-compliant documentation']
            : ['Scoped to your business size', 'Hands-on rollout and training', 'Documentation for your team'],
        right: isConstruction
            ? ['Cost containment vs. architect-only plans', 'Vendor & sub coordination', 'PM training at handoff']
            : ['Vendor & license management', 'Ongoing monitoring & support', 'Quarterly review & iteration'],
    };


    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content service-single-page">
                <PageTitle pageTitle={ServiceDetails.seoTitle || ServiceDetails.title} pagesub={'Details '} pageTop={'Services'} />
                <section className="service_details_section section_space bg-light">
                    <div className="container">
                        <div className="details_item_image position-relative" style={{ overflow: 'hidden', borderRadius: 'var(--bs-border-radius-sm)' }}>
                            {slug === 'AI-Business-Integration' ? (
                                <video
                                    src={`${process.env.PUBLIC_URL}/videos/buildlab_ai.mp4`}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    style={{ width: '100%', height: 'auto', display: 'block', background: '#000' }}
                                />
                            ) : (
                                <img src={HERO_BY_SLUG[slug] || srImg} alt={ServiceDetails.title} />
                            )}
                        </div>
                        <h2 className="details_item_title">{ServiceDetails.title}</h2>
                        {slug === 'AI-Business-Integration' ? (
                            <>
                                <p>
                                    AI Business Integration is BUILDLAB's hands-on practice for embedding AI into the way your team actually works. We start by mapping your real workflows — the manual reports, repetitive emails, lookups, approvals, and handoffs that eat hours every week — then identify exactly where AI assistants, copilots, and lightweight automation can shave those hours. The hard part isn't picking a tool; the hard part is figuring out where to start, which tool to use, and how to train your team so it actually sticks.
                                </p>
                                <p>
                                    We're tool-agnostic on purpose. Whether the right fit is Microsoft Copilot, Claude, ChatGPT Business, Notion AI, custom GPTs, or a purpose-built automation, we'll recommend what your team can adopt without re-platforming your business. Every engagement includes governance and guardrails — what data is safe to share, how prompts get reviewed, who owns the outputs — so AI becomes a daily habit, not a novelty that fades after the demo.
                                </p>
                                <ProcessTechnology steps={AI_PROCESS_STEPS} aiMode={true} />
                                <h3 className="details_item_info_title">Outcomes you can measure</h3>
                                <p className="mb-4">
                                    Every AI Business Integration engagement is scoped to deliver concrete, measurable wins for your operations — not abstract "digital transformation" talking points.
                                </p>
                                <div className="row mb-4">
                                    <div className="col-lg-6">
                                        <ul className="icon_list unordered_list_block">
                                            <li>
                                                <span className="icon_list_icon">
                                                    <img src={icon} alt="Check SVG Icon"/>
                                                </span>
                                                <span className="icon_list_text">
                                                    Workflow audit &amp; opportunity map
                                                </span>
                                            </li>
                                            <li>
                                                <span className="icon_list_icon">
                                                    <img src={icon} alt="Check SVG Icon"/>
                                                </span>
                                                <span className="icon_list_text">
                                                    Tool &amp; stack recommendations
                                                </span>
                                            </li>
                                            <li>
                                                <span className="icon_list_icon">
                                                    <img src={icon} alt="Check SVG Icon"/>
                                                </span>
                                                <span className="icon_list_text">
                                                    Quantified hours-saved estimate per role
                                                </span>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className="col-lg-6">
                                        <ul className="icon_list unordered_list_block">
                                            <li>
                                                <span className="icon_list_icon">
                                                    <img src={icon} alt="Check SVG Icon"/>
                                                </span>
                                                <span className="icon_list_text">
                                                    Hands-on team training &amp; prompt patterns
                                                </span>
                                            </li>
                                            <li>
                                                <span className="icon_list_icon">
                                                    <img src={icon} alt="Check SVG Icon"/>
                                                </span>
                                                <span className="icon_list_text">
                                                    Governance, data privacy &amp; guardrails
                                                </span>
                                            </li>
                                            <li>
                                                <span className="icon_list_icon">
                                                    <img src={icon} alt="Check SVG Icon"/>
                                                </span>
                                                <span className="icon_list_text">
                                                    Written roadmap you can act on
                                                </span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                {introParagraphs.map((para, i) => (
                                    <p key={i}>{para}</p>
                                ))}
                                <ProcessTechnology steps={PROCESS_STEPS_BY_SLUG[slug]} hideTech={HIDE_TECH_SLUGS.includes(slug)} />
                                {slug === 'Surveillance-Systems' && <SurveillanceBrands />}
                                <h3 className="details_item_info_title">What you can expect</h3>
                                <p className="mb-4">
                                    {expect.intro}
                                </p>
                                <div className="row mb-4">
                                    <div className="col-lg-6">
                                        <ul className="icon_list unordered_list_block">
                                            {expect.left.map((text, i) => (
                                                <li key={i}>
                                                    <span className="icon_list_icon"><img src={icon} alt="Check"/></span>
                                                    <span className="icon_list_text">{text}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="col-lg-6">
                                        <ul className="icon_list unordered_list_block">
                                            {expect.right.map((text, i) => (
                                                <li key={i}>
                                                    <span className="icon_list_icon"><img src={icon} alt="Check"/></span>
                                                    <span className="icon_list_text">{text}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </>
                        )}
                        <div className="row">
                            {detailTrio.map((im, i) => (
                                <div className="col-lg-4 col-md-6 col-sm-6" key={i}>
                                    <div className="details_item_image m-0">
                                        <img src={im.src} alt={im.alt} />
                                    </div>
                                </div>
                            ))}
                        </div>
                        {RELATED_TOOLS_BY_SLUG[slug] && (
                            <div className="mt-4 p-4" style={{ background: '#F1F6FC', borderRadius: 14 }}>
                                <h3 className="details_item_info_title mb-3" style={{ fontSize: '1.15rem' }}>Related free tools</h3>
                                <ul className="icon_list unordered_list_block mb-0">
                                    {RELATED_TOOLS_BY_SLUG[slug].map((t) => (
                                        <li key={t.to}>
                                            <Link to={t.to}>
                                                <span className="icon_list_icon"><i className="fa-solid fa-arrow-right" aria-hidden="true" style={{ color: 'var(--bs-primary)', fontSize: '.7rem' }}></i></span>
                                                <span className="icon_list_text">{t.label}</span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {slug === 'Web-Development' && <WebDevPromo />}
                        {(slug === 'Web-Development' || slug === 'Marketing-and-SEO') && (
                            <div className="mt-5 p-4 p-lg-5" style={{ background: 'var(--bs-dark)', borderRadius: 18 }}>
                                <div className="row align-items-center" style={{ rowGap: 14 }}>
                                    <div className="col-lg-8">
                                        <h3 style={{ color: '#fff', marginBottom: 8 }}>See how your site scores — free.</h3>
                                        <p style={{ color: 'rgba(255,255,255,0.82)', margin: 0 }}>
                                            Run an instant SEO, speed, and accessibility audit and get a prioritized list of fixes — in about 30 seconds.
                                        </p>
                                    </div>
                                    <div className="col-lg-4 text-lg-end">
                                        <Link to="/website-audit" className="btn btn-primary">
                                            <span className="btn_label" data-text="Free Website Audit">Free Website Audit</span>
                                            <span className="btn_icon"><i className="fa-solid fa-arrow-up-right" aria-hidden="true"></i></span>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </main>
            <CtaSection />
            <Footer />
            <Scrollbar />
        </Fragment>
    )
};
export default ServiceSinglePage;